import React, { useState, useEffect, useMemo } from 'react';
import { TextBoxComponent, NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent, MultiSelectComponent } from '@syncfusion/ej2-react-dropdowns';
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { FormDialog } from '../../components/FormDialog';
import { JexlCodeEditor } from '../../components/JexlCodeEditor';
import type { CrudFormProps } from '../../components/CrudPage';
import { useTranslation } from 'react-i18next';
import { SystemSettingControllerService } from '../../api/services/SystemSettingControllerService';
import { demandEntityFields } from '../../config/entityFields';
import { MaterialControllerService } from '../../api/services/MaterialControllerService';
import type { MaterialDto } from '../../api/models/MaterialDto';
import { DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX } from '../../utils/demandCsvImportPipeline';

export type CsvImportConfigFormValues = {
  id: string;
  entityType: string;
  description?: string;
  separator?: string;
  decimalSeparator?: string;
  encoding?: string;
  skipRows?: number;
  fieldMapping: Record<string, string>;
  valueMappers: Record<string, { function?: string; format?: string; strategy?: string; sourceColumn?: string }>;
  defaultValues?: Record<string, string>;
  rowFilter?: string;
  /** Field names that are prompted at import start (run parameters). */
  runParameterFields?: string[];
  /** Demand CSV: empty = all materials importable. */
  demandImportAllowedMaterialUuids?: string[];
  /** Demand CSV: weeks of FORECAST derived from ORDER import (0 = off, max 10). */
  demandImportOrderForecastWeeksAhead?: number;
};

const CsvImportConfigFormDialog: React.FC<CrudFormProps<CsvImportConfigFormValues>> = ({
  value,
  onClose,
  onSave
}) => {
  const { t } = useTranslation(['common']);
  const [deliveryTimes, setDeliveryTimes] = useState<string[]>([]);
  const [materialOptions, setMaterialOptions] = useState<MaterialDto[]>([]);

  useEffect(() => {
    MaterialControllerService.findAll()
      .then((list) => setMaterialOptions(Array.isArray(list) ? list : []))
      .catch(() => setMaterialOptions([]));
  }, []);

  useEffect(() => {
    SystemSettingControllerService.getDeliveryTimes()
      .then((times) => setDeliveryTimes(Array.isArray(times) ? times : []))
      .catch(() => setDeliveryTimes([]));
  }, []);

  const availableEncodings = [
    { value: 'UTF-8', text: 'UTF-8' },
    { value: 'Windows-1252', text: 'Windows-1252 (ANSI)' },
    { value: 'ISO-8859-1', text: 'ISO-8859-1 (Latin-1)' }
  ];

  const availableDelimiters = [
    { value: ',', text: ',' },
    { value: ';', text: ';' },
    { value: '\t', text: '\\t' },
    { value: '|', text: '|' },
    { value: ':', text: ':' }
  ];

  const availableDecimalSeparators = [
    { value: '.', text: '.' },
    { value: ',', text: ',' }
  ];

  const entityOptions = [
    { value: 'demands', text: t('routes.demand') }
  ];

  const conversionOptions = [
    { value: '', text: '' },
    { value: 'parseDate', text: 'parseDate' },
    { value: 'parseDateTime', text: 'parseDateTime' },
    { value: 'extractDate', text: 'extractDate' },
    { value: 'extractTime', text: 'extractTime' },
    { value: 'parseInteger', text: 'parseInteger' },
    { value: 'mapDemandSource', text: 'mapDemandSource' }
  ];
  const formatRequiredConverters = new Set(['parseDate', 'parseDateTime', 'extractDate', 'extractTime']);

  const materialMultiSelectData = useMemo(
    () =>
      [...materialOptions]
        .filter((m) => m.uuid)
        .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' }))
        .map((m) => ({ text: m.name ?? m.uuid ?? '', value: m.uuid as string })),
    [materialOptions]
  );

  return (
    <FormDialog
      value={value}
      onClose={onClose}
      onSave={onSave}
      titleKey="csvImport.title"
      width="min(1280px, 95vw)"
    >
      {({ register, watch, setValue }) => {
        const currentValue = watch();
        const entityType = currentValue.entityType || 'demands';

        const fields = entityType === 'demands'
          ? demandEntityFields.filter((field) => field.field !== 'status')
          : [];
        const getFieldLabel = (field: typeof demandEntityFields[number]) =>
          t(`demand.grid.field.${field.field}`, field.label);
        const isMaterialLookupField = (field: typeof demandEntityFields[number]) => field.field === 'materialLookup';

        return (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <DropDownListComponent
                dataSource={entityOptions}
                fields={{ text: 'text', value: 'value' }}
                value={entityType}
                change={(e) => {
                  setValue('entityType', e.value);
                }}
                placeholder={`${t('csvImport.entityType')} *`}
                floatLabelType="Auto"
              />
            </div>

            <div className="mb-4">
              <TextBoxComponent
                {...register('description')}
                placeholder={t('csvImport.description')}
                floatLabelType="Auto"
                multiline={true}
              />
            </div>

            {entityType === 'demands' && (
              <>
                <div className="mb-4">
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    {t('csvImport.demandImportAllowedMaterialsLabel', 'Zugelassene Materialien (optional)')}
                    <span className="cursor-help text-gray-400" title={t('csvImport.demandImportAllowedMaterialsHelp', 'Keine Auswahl: alle Produkte aus der CSV werden importiert.')}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </label>
                  <MultiSelectComponent
                    dataSource={materialMultiSelectData}
                    fields={{ text: 'text', value: 'value' }}
                    value={currentValue.demandImportAllowedMaterialUuids ?? []}
                    mode="CheckBox"
                    showSelectAll={true}
                    locale="de"
                    floatLabelType="Never"
                    change={(e: { value?: string[] }) =>
                      setValue('demandImportAllowedMaterialUuids', e.value ?? [])
                    }
                  />
                </div>
                <div className="mb-4">
                  <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    {t('csvImport.demandImportOrderForecastWeeksAhead', 'Prognosen aus Bestellungen (Wochen)')}
                    <span className="cursor-help text-gray-400" title={t('csvImport.demandImportOrderForecastWeeksAheadHelp', 'Beim Import von Bestellungen: gleicher Wochentag, 1 bis N Wochen in die Zukunft als Prognose (überschreibt vorhandene). 0 = aus.')}>
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </label>
                  <NumericTextBoxComponent
                    value={currentValue.demandImportOrderForecastWeeksAhead ?? 0}
                    min={0}
                    max={DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX}
                    strictMode={true}
                    format="n0"
                    floatLabelType="Never"
                    change={(e: { value: number | null }) =>
                      setValue(
                        'demandImportOrderForecastWeeksAhead',
                        e.value != null
                          ? Math.max(0, Math.min(DEMAND_IMPORT_ORDER_FORECAST_WEEKS_MAX, Math.floor(e.value)))
                          : 0
                      )
                    }
                  />
                </div>
              </>
            )}

            <div className="grid grid-cols-3 gap-4 mb-4">
              <DropDownListComponent
                dataSource={availableDelimiters}
                fields={{ text: 'text', value: 'value' }}
                value={currentValue.separator || ';'}
                change={(e) => setValue('separator', e.value)}
                placeholder={t('csvImport.separator')}
                floatLabelType="Auto"
              />
              <DropDownListComponent
                dataSource={availableDecimalSeparators}
                fields={{ text: 'text', value: 'value' }}
                value={currentValue.decimalSeparator || ','}
                change={(e) => setValue('decimalSeparator', e.value)}
                placeholder={t('csvImport.decimalSeparator')}
                floatLabelType="Auto"
              />
              <DropDownListComponent
                dataSource={availableEncodings}
                fields={{ text: 'text', value: 'value' }}
                value={currentValue.encoding || 'UTF-8'}
                change={(e) => setValue('encoding', e.value)}
                placeholder={t('csvImport.encoding')}
                floatLabelType="Auto"
              />
            </div>

            <div className="mb-4">
              <NumericTextBoxComponent
                {...register('skipRows', { valueAsNumber: true })}
                placeholder={t('csvImport.skipRows')}
                floatLabelType="Auto"
                format="n0"
                min={0}
              />
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                {t('csvImport.rowFilter', 'Zeilenfilter')}
                <span className="cursor-help text-gray-400" title={`${t('csvImport.rowFilterHelp', 'Nur Zeilen, für die der Ausdruck true ergibt, werden importiert. Kontext = Spaltennamen.')} z.B. Fertigungsort == '141 Brot Abteilung'`}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </span>
              </label>
              <JexlCodeEditor
                value={currentValue.rowFilter ?? ''}
                onChange={(v) => setValue('rowFilter', v)}
                placeholder=""
                defaultValueStyle={false}
                multiline={true}
              />
            </div>

            <div className="mb-4">
              <div className="grid gap-2 text-sm font-semibold text-gray-700 mb-1" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                <div className="col-span-4 min-w-0">{t('csvImport.fieldMapping')}</div>
                <div className="col-span-3 min-w-0">{t('csvImport.csvColumn')}</div>
                <div className="col-span-3 min-w-0 flex items-center gap-0.5">
                  {t('csvImport.conversionFunction')}
                  <span className="cursor-help text-gray-400 shrink-0" title={t('csvImport.conversionHelp', 'Optionale Konvertierung des CSV-Werts, z.B. parseInteger, parseDate.')}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
                <div className="col-span-3 min-w-0">{t('csvImport.conversionFormat')}</div>
                <div className="col-span-3 min-w-0 flex items-center gap-0.5">
                  {t('csvImport.lookupStrategy', 'Lookup-Typ')}
                  <span className="cursor-help text-gray-400 shrink-0" title={t('csvImport.lookupHelp', 'Nur bei Material-Lookup: Artikelnummer oder Name. Konvertierung bereitet den Wert vor (z.B. parseInteger).')}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
                <div className="col-span-4 min-w-0 flex items-center justify-center gap-0.5 whitespace-nowrap">
                  {t('csvImport.runParameter', 'Laufzeit-Parameter')}
                  <span className="cursor-help text-gray-400 shrink-0" title={t('csvImport.runParameterHelp', 'Wenn aktiv: Beim Import wird der Wert einmal pro Lauf abgefragt (z.B. Bedarfsdatum). Sonst gilt die CSV-Spalte oder der Default.')}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
                <div className="col-span-4 min-w-0 flex items-center gap-0.5">
                  {t('csvImport.defaultValue', 'Default')}
                  <span className="cursor-help text-gray-400 shrink-0" title={t('csvImport.defaultValueHelp', 'Statischer Text: wird 1:1 übernommen (z.B. BBN). Ausdruck: nur in {# ... } wird als Jexl ausgewertet, z.B. {#today(1)} für morgen. Bei Fälligkeitszeit: Auswahl aus Auslieferzeiten.')}>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                {fields.map((field) => {
                  const selectedConverter = currentValue.valueMappers?.[field.field]?.function || '';
                  const requiresFormat = formatRequiredConverters.has(selectedConverter);
                  const runParamList = Array.isArray(currentValue.runParameterFields) ? currentValue.runParameterFields : [];
                  const isRunParameter = runParamList.includes(field.field);
                  const isDueTimeField = field.field === 'dueTime';

                  return (
                    <div key={field.field} className="grid gap-2 items-center min-h-[2rem]" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
                      <div className="col-span-4 min-w-0 flex items-center">
                        <span className="text-sm text-gray-900 truncate" title={field.field}>
                          {getFieldLabel(field)}{field.required ? ' *' : ''}
                        </span>
                      </div>
                      <div className="col-span-3 min-w-0">
                        <TextBoxComponent
                          {...register(`fieldMapping.${field.field}`)}
                          placeholder=""
                          floatLabelType="Never"
                        />
                      </div>
                      <div className="col-span-3 min-w-0">
                        <DropDownListComponent
                          dataSource={conversionOptions}
                          fields={{ text: 'text', value: 'value' }}
                          value={selectedConverter}
                          change={(e) => setValue(`valueMappers.${field.field}.function`, e.value)}
                          placeholder=""
                          floatLabelType="Never"
                        />
                      </div>
                      <div className="col-span-3 min-w-0">
                        <TextBoxComponent
                          {...register(`valueMappers.${field.field}.format`)}
                          placeholder=""
                          floatLabelType="Never"
                          enabled={requiresFormat}
                        />
                      </div>
                      <div className="col-span-3 min-w-0">
                        {isMaterialLookupField(field) ? (
                          <DropDownListComponent
                            dataSource={[
                              { value: 'articleNumber', text: 'Artikelnummer' },
                              { value: 'name', text: 'Name' }
                            ]}
                            fields={{ text: 'text', value: 'value' }}
                            value={currentValue.valueMappers?.[field.field]?.strategy || 'articleNumber'}
                            change={(e) => setValue(`valueMappers.${field.field}.strategy`, e.value)}
                            placeholder=""
                            floatLabelType="Never"
                          />
                        ) : null}
                      </div>
                      <div className="col-span-4 flex items-center justify-center min-w-0">
                        <CheckBoxComponent
                          checked={isRunParameter}
                          change={(e: any) => {
                            const checked = e.checked === true;
                            const next = checked
                              ? [...runParamList, field.field]
                              : runParamList.filter((f) => f !== field.field);
                            setValue('runParameterFields', next);
                          }}
                        />
                      </div>
                      <div className="col-span-4 min-w-0">
                        {isDueTimeField && !isRunParameter && deliveryTimes.length > 0 ? (
                          <DropDownListComponent
                            dataSource={deliveryTimes.map((timeStr) => ({ value: timeStr.slice(0, 5), text: timeStr.slice(0, 5) }))}
                            fields={{ text: 'text', value: 'value' }}
                            value={currentValue.defaultValues?.[field.field]?.slice(0, 5) ?? (deliveryTimes[0]?.slice(0, 5) ?? '')}
                            change={(e) => setValue(`defaultValues.${field.field}`, e.value ?? '')}
                            placeholder=""
                            floatLabelType="Never"
                          />
                        ) : isRunParameter ? (
                          <span className="text-xs text-gray-500 whitespace-nowrap">{t('csvImport.runParameterPrompted', 'Wird beim Import abgefragt')}</span>
                        ) : (
                          <JexlCodeEditor
                            value={currentValue.defaultValues?.[field.field] ?? ''}
                            onChange={(v) => setValue(`defaultValues.${field.field}`, v)}
                            placeholder=""
                            defaultValueStyle={true}
                            multiline={false}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        );
      }}
    </FormDialog>
  );
};

export default CsvImportConfigFormDialog;
